import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Sparkles, ChevronDown, ChevronUp,
  BarChart3, BookOpen, Users, Zap, Heart,
  Globe, FileText, Stethoscope, CheckCircle
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import StoreBadges, { APP_STORE_URL } from './components/StoreBadges';
import StarterKitSignup from './components/StarterKitSignup';
import usePlatform from './utils/usePlatform';
import { HOME_PORTALS } from './data/homePortals';
import { applyHead } from './utils/applyHead';
import { appUrl } from './utils/appUrl';
import { storeUrl } from './utils/storeUrl';
import { AppScreenshots } from './components/AppScreenshots';
import PassionShorts from './components/PassionShorts';
import HomeShowcase from './components/HomeShowcase';

const HOME_TITLE = 'Recalibrate | Symptom Tracking & Health Education';
const HOME_DESC = 'Track symptoms, understand patterns and prepare for care with Recalibrate. Health education and tools in one app. Passion, coming soon: Scroll Shorts for chronic pain and chronic illness. Start free on iPhone, Android or in your browser.';

function App() {
  const heroBgRef = useRef(null);
  const heroContentRef = useRef(null);
  const heroGlowRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);  const [visibleSections, setVisibleSections] = useState({});
  const platform = usePlatform();
  const isAndroid = platform === 'android';
  const isIos = platform === 'ios';

  // Synchronous head injection - guarantees the home title/description are in
  // the DOM before paint, so react-snap reliably captures them at build time.
  useLayoutEffect(() => { applyHead(HOME_TITLE, HOME_DESC, 'https://www.recalibratepain.com'); }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px 12% 0px' }
    );
    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Scroll-SCRUBBED phone assembly. The phone's 3D spin, the label fly-ins and
  // their opacity are driven directly by how far the section has travelled
  // through the viewport - so scrolling down spins everything INTO place and
  // scrolling back up spins it OUT. Continuous, reversible, layered.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // On mobile/tablet the per-frame rotateY/scale scrub is the main cause of
    // glitchy, laggy scrolling. Skip the scroll scrub there and render the
    // phone + labels in their final assembled state (same as reduced-motion).
    const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 1024px)').matches;
    const section = document.getElementById('ecosystem-tour');
    if (!section) return undefined;
    const phones = Array.from(document.querySelectorAll('[data-phone-spin]'));
    const labels = Array.from(document.querySelectorAll('[data-label-fly]'));
    const heads = Array.from(document.querySelectorAll('[data-tour-head]'));
    const ctas = Array.from(document.querySelectorAll('[data-tour-cta]'));

    if (reduce || isMobile) {
      phones.forEach(p => { p.style.transform = 'none'; p.style.opacity = '1'; });
      labels.forEach(l => { l.style.opacity = '1'; });
      heads.forEach(h => { h.style.opacity = '1'; h.style.transform = 'none'; });
      ctas.forEach(c => { c.style.opacity = '1'; c.style.transform = 'none'; });
      return undefined;
    }

    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Master progress is derived from the VISIBLE phone's on-screen position
      // (its untransformed wrapper), so the spin behaves IDENTICALLY on desktop
      // and mobile regardless of the section's differing height: 0 as the phone
      // enters from the bottom, 1 (straight) once it is centred in the reading
      // zone - well before you scroll past the "Explore the ecosystem" button.
      let progress = 0;
      const visiblePhone = phones.find(p => p.parentElement && p.parentElement.getBoundingClientRect().width > 0);
      if (visiblePhone) {
        const wr = visiblePhone.parentElement.getBoundingClientRect();
        const center = wr.top + wr.height / 2;
        progress = Math.min(Math.max((vh * 1.08 - center) / (vh * 0.66), 0), 1);
      }
      const e = easeOut(progress);
      const labelDist = window.innerWidth < 640 ? 70 : 280;

      phones.forEach(p => {
        const rotate = (1 - e) * -720;
        const scale = 0.5 + e * 0.5;
        const ty = (1 - e) * 90;
        p.style.transform = `translateY(${ty}px) rotateY(${rotate}deg) scale(${scale})`;
        p.style.opacity = String(Math.min(progress * 4, 1));
      });
      labels.forEach((l) => {
        const idx = parseInt(l.dataset.idx || '0', 10);
        const dir = l.dataset.side === 'left' ? -1 : 1;
        const delay = Math.min(idx * 0.07, 0.45);
        const lp = Math.min(Math.max((progress - delay) / (1 - delay), 0), 1);
        const le = easeOut(lp);
        const tx = (1 - le) * dir * labelDist;
        const ty = (1 - le) * 30;
        const rot = (1 - le) * dir * 18;
        const ls = 0.6 + le * 0.4;
        l.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg) scale(${ls})`;
        l.style.opacity = String(Math.min(lp * 3, 1)); // opacity resolves fast so the motion (not a fade) reads
      });
      // Heading + subcopy clearly slide+scale UP as the section arrives (scroll
      // linked), so the writing animates in rather than just sitting there.
      const hp = Math.min(Math.max((vh * 0.95 - rect.top) / (vh * 0.5), 0), 1);
      const he = easeOut(hp);
      heads.forEach(h => {
        h.style.transform = `translateY(${(1 - he) * 72}px) scale(${0.96 + he * 0.04})`;
        h.style.opacity = String(he);
      });
      const cp = Math.min(Math.max((progress - 0.45) / 0.45, 0), 1);
      const ce = easeOut(cp);
      ctas.forEach(c => {
        c.style.transform = `translateY(${(1 - ce) * 28}px) scale(${0.92 + ce * 0.08})`;
        c.style.opacity = String(ce);
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    // Safety net: if the section scrolls into view on a slow connection (or
    // the visiblePhone selector fails on first run), force everything visible
    // after 2.5 s so the phone never stays permanently invisible.
    const showAll = () => {
      phones.forEach(p => { p.style.transform = 'none'; p.style.opacity = '1'; });
      labels.forEach(l => { l.style.opacity = '1'; l.style.transform = 'none'; });
      heads.forEach(h => { h.style.opacity = '1'; h.style.transform = 'none'; });
      ctas.forEach(c => { c.style.opacity = '1'; c.style.transform = 'none'; });
    };
    const fallbackTimer = setTimeout(() => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Only force-show if section is already in view (i.e. user has scrolled to it)
      if (rect.top < vh * 0.9 && rect.bottom > 0) {
        showAll();
      }
    }, 2500);

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll-driven hero parallax: the dark background layers linger while the
  // content drifts up and fades, so the lighter section beneath rises and
  // overlaps it - the top of the page visibly transforms as you scroll.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    // Skip scroll-driven hero parallax on mobile/tablet - the per-frame
    // transform+opacity writes on the hero layers cause visible scroll jank
    // on touch devices. Desktop keeps the effect.
    if (window.matchMedia && window.matchMedia('(max-width: 1024px)').matches) return undefined;
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      const vh = window.innerHeight || 1;
      if (y > vh * 1.25) return; // only do work within the hero range
      const p = Math.min(y / vh, 1);
      if (heroBgRef.current) {
        heroBgRef.current.style.transform = `translate3d(0, ${y * 0.35}px, 0)`;
      }
      if (heroGlowRef.current) {
        heroGlowRef.current.style.transform = `translate3d(-50%, calc(-50% + ${y * 0.45}px), 0) scale(${1 + p * 0.3})`;
      }
      if (heroContentRef.current) {
        heroContentRef.current.style.transform = `translate3d(0, ${y * 0.12}px, 0)`;
        heroContentRef.current.style.opacity = `${Math.max(1 - p * 1.2, 0)}`;
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const faqs = [
    { q: 'What is Recalibrate?', a: "Recalibrate is a comprehensive health ecosystem built for patients, clinicians and carers. It is the world's first all-in-one allied health platform designed specifically for chronic pain, fibromyalgia, long COVID, ME-CFS, ADHD and other long-term health conditions. Recalibrate combines advanced health tracking, a Neuroscience Academy with 200+ lessons, therapeutic tools, AI-powered insights, medication management and a connected Care Team portal all in one place." },
    { q: 'What does Recalibrate do?', a: "Recalibrate helps you understand, track and manage your health. You can track 18+ health variables daily, learn evidence-based pain science through 200+ Academy lessons, use therapeutic tools like CBT, mindfulness and guided exercises, chat with Recalibrate AI about your health data and generate graphs and reports, track medications with smart reminders, journal and set goals, and connect your clinicians and carers so they can see your real-time health data and maximise every appointment." },
    { q: 'Is Recalibrate available now?', a: "Yes. Recalibrate is live on the App Store and Google Play. Download it free on iPhone or Android, or use the full web app at recalibratepain.app. Every new subscriber gets 7 days free to try everything, and the free tier stays free." },
    { q: 'What can Recalibrate AI actually do?', a: "Recalibrate AI is your personal health companion. You can chat directly about your tracked health data, ask it to generate visual graphs and reports, get personalised pattern analysis, research pain science topics, and receive evidence-based suggestions tailored to your health journey. It is available 24/7 and gets smarter the more you use it." },
    { q: 'What makes Recalibrate different from other health apps?', a: "Recalibrate is not just another tracker. It is an entire health ecosystem, the first of its kind, that replaces what would normally take five or more separate apps. You get advanced tracking across 18+ health variables with custom metrics like Stability Score, a Neuroscience Academy with 200+ lessons, therapeutic tools including CBT, mindfulness and guided exercises, AI-powered insights and data chat, journaling, goal tracking, medication management, and a connected Care Team portal where clinicians, psychologists, pain specialists and carers can access real-time patient data, maximising every appointment." },
    { q: 'Is Recalibrate free?', a: "There is a free tier - you get the Initial and Level 2 AI assessment, 10 tracking entries a month, Daily Habits + Streak, the Academy Course 1 intro and 5 Recalibrate AI credits a month. Unlimited tracking, the Stability Dashboard, monthly Level 2 reassessments (PDFs included) and the 136-lesson Academy foundations unlock at Recalibrate Go ($12.99/mo or $99.99/yr). Recalibrate Pro ($34.99/mo or $299.99/yr) adds the Academy Pro Courses (200+ lessons), Therapeutic Tools, AI Journal, medications and reminders, Brain Games, predictive analytics and a 2 clinician + 2 carer Care Team. New users can claim 3 months of Pro for $34.99 (pay 1, get 3) via the Welcome Offer." },
    { q: 'Who is Recalibrate for?', a: "Recalibrate is built for three audiences. Patients living with chronic pain, fibromyalgia, long COVID, ME-CFS, ADHD, central sensitisation and other long-term health conditions. Clinicians including physiotherapists, psychologists, pain specialists and allied health professionals who want real-time patient data and analytics. And carers and family members who want to support their loved ones with shared health dashboards." },
    { q: 'Is my health data private on Recalibrate?', a: "100% private. Your data is encrypted, never sold and never shared. You own your data completely. We follow strict industry-standard security practices and will never monetise your health information." },
  ];

  // Each hub card has:
  //   gradient   - the Tailwind gradient classes for header + icon disc
  //   accent     - short colour name (kept for back-compat)
  //   accentRgb  - rgba(...) used as the CSS custom prop --hub-accent;
  //                drives the icon-disc halo, the hover bloom shadow and
  //                the per-card colour on "Explore →".
  //   accentText - Tailwind text-* class so the "Explore →" link wears the
  //                card's own colour instead of a uniform purple.


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-indigo-50/50">
      <Helmet>
        <title>Recalibrate - A Health & Neuroscience Ecosystem for Patients, Clinicians & Carers</title>
        <meta name="description" content="Recalibrate is a health and neuroscience ecosystem: a tracking app, evidence-based blog, condition guides, courses, a clinician platform and a connected care team. Learn how your brain and body actually work - especially helpful for chronic pain, fibromyalgia, long COVID, ME-CFS, ADHD and other long-term conditions, but built for anyone curious about their health. Passion, coming soon: Scroll Shorts for chronic pain and chronic illness." />
        <meta name="keywords" content="health and neuroscience ecosystem, neuroscience app, brain and body health, learn neuroscience, evidence-based health blog, condition guides, chronic pain, fibromyalgia, long COVID, ME-CFS, ADHD, central sensitisation, allied health platform, clinician dashboard, multidisciplinary care, patient clinician carer platform, pain neuroscience education, sleep neuroscience, stress nervous system, vagus nerve, pacing chronic illness, evidence-based wellness" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.recalibratepain.com" />
        <meta property="og:title" content="Recalibrate - A Health & Neuroscience Ecosystem for Patients, Clinicians & Carers" />
        <meta property="og:description" content="A health & neuroscience ecosystem: tracking app, evidence-based blog, condition guides, courses, clinician platform and connected care. Especially helpful for chronic pain, long COVID and long-term illness - built for anyone curious about how their brain and body work." />
        <meta property="og:url" content="https://www.recalibratepain.com" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Recalibrate" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://www.recalibratepain.com/social-preview.jpg?v=4" />
        <meta property="og:image:secure_url" content="https://www.recalibratepain.com/social-preview.jpg?v=4" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Recalibrate | A Health & Neuroscience Ecosystem" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RecalibrateApp" />
        <meta name="twitter:creator" content="@RecalibrateApp" />
        <meta name="twitter:title" content="Recalibrate - A Health & Neuroscience Ecosystem for Patients, Clinicians & Carers" />
        <meta name="twitter:description" content="A health & neuroscience ecosystem: tracking app, evidence-based blog, condition guides, courses, clinician platform and connected care. Especially helpful for chronic pain, long COVID and long-term illness." />
        <meta name="twitter:image" content="https://www.recalibratepain.com/social-preview.jpg?v=4" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Recalibrate Free Health Portals",
          "description": "Free patient intelligence portals for fibromyalgia, long COVID, ME-CFS, ADHD and central sensitisation syndrome.",
          "url": "https://www.recalibratepain.com/#health-portals-section",
          "numberOfItems": HOME_PORTALS.length,
          "itemListElement": [
            ...HOME_PORTALS.map((portal, index) => ({ "@type": "ListItem", "position": index + 1, "name": portal.name, "url": new URL(portal.hub, process.env.REACT_APP_SITE_URL).href }))
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Recalibrate",
          "url": "https://www.recalibratepain.com",
          "description": HOME_DESC
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Recalibrate",
          "applicationCategory": "HealthApplication",
          "applicationSubCategory": "Chronic Pain Management",
          "operatingSystem": ["Web", "iOS", "Android"],
          "url": "https://recalibratepain.app",
          "installUrl": "https://play.google.com/store/apps/details?id=com.recalibratepain.app",
          "downloadUrl": "https://play.google.com/store/apps/details?id=com.recalibratepain.app",
          "description": "Comprehensive allied health platform with symptom tracking (18+ variables), Neuroscience Academy (200+ lessons), AI health companion, therapeutic tools, journaling, medication tracking, and connected care team for patients, clinicians and carers.",
          "featureList": ["Symptom Tracking", "Analytics Dashboard", "Neuroscience Academy", "AI Health Companion", "Therapeutic Tools", "Journal & Goal Tracking", "Medication Tracking", "Care Team Connection", "Clinician Dashboard", "Carer Portal"],
          "screenshot": "https://www.recalibratepain.com/app-screenshot-optimized.jpg",
          "offers": [
            { "@type": "Offer", "name": "Welcome Offer · 3 months of Pro", "price": "34.99", "priceCurrency": "USD", "description": "Pay $34.99 once, get 3 months of Recalibrate Pro. Then $34.99/mo on day 91. Cancel any time." },
            { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD", "description": "Initial + Level 2 AI assessment, 10 tracking entries/mo, Daily Habits + Streak, Academy Course 1 intro, 5 AI credits/mo." },
            { "@type": "Offer", "name": "Recalibrate Go", "price": "12.99", "priceCurrency": "USD", "billingIncrement": "P1M", "description": "Unlimited tracking, Wellness Check, Stability Dashboard, monthly Level 2 reassessment with PDFs included, Academy foundations (136 lessons), Care Team (1 clinician + 1 carer), 25 AI credits/mo." },
            { "@type": "Offer", "name": "Recalibrate Pro", "price": "34.99", "priceCurrency": "USD", "billingIncrement": "P1M", "description": "Everything in Go plus Academy Pro Courses (200+ lessons), Therapeutic Tools, AI Journal, medications and reminders, Brain Games, predictive analytics, Care Team (2 clinicians + 2 carers), 50 AI credits/mo." }
          ],
          "author": { "@type": "Organization", "name": "Recalibrate Inc.", "url": "https://www.recalibratepain.com" },
          "audience": { "@type": "MedicalAudience", "audienceType": ["Patient", "Clinician", "Carer", "Researcher"] },
          "keywords": "chronic pain app, fibromyalgia tracker, long COVID management, ME-CFS app, ADHD health tracking, central sensitisation, pain management, health tracking, pain science, allied health platform"
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is Recalibrate?", "acceptedAnswer": { "@type": "Answer", "text": "Recalibrate is a comprehensive health ecosystem built for patients, clinicians and carers. It is the world's first all-in-one allied health platform designed specifically for chronic pain, fibromyalgia, long COVID, ME-CFS, ADHD and other long-term health conditions. Recalibrate combines advanced health tracking, a Neuroscience Academy with 200+ lessons, therapeutic tools, AI-powered insights, medication management and a connected Care Team portal all in one place." } },
            { "@type": "Question", "name": "What does Recalibrate do?", "acceptedAnswer": { "@type": "Answer", "text": "Recalibrate helps you understand, track and manage your health. You can track 18+ health variables daily, learn evidence-based pain science through 200+ Academy lessons, use therapeutic tools like CBT, mindfulness and guided exercises, chat with Recalibrate AI about your health data and generate graphs and reports, track medications with smart reminders, journal and set goals, and connect your clinicians and carers so they can see your real-time health data and maximise every appointment." } },
            { "@type": "Question", "name": "Is Recalibrate available now?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Recalibrate is live on the App Store and Google Play. Download it free on iPhone or Android, or use the full web app at recalibratepain.app. Every new subscriber gets 7 days free to try everything, and the free tier stays free." } },
            { "@type": "Question", "name": "What can Recalibrate AI actually do?", "acceptedAnswer": { "@type": "Answer", "text": "Recalibrate AI is your personal health companion. You can chat directly about your tracked health data, ask it to generate visual graphs and reports, get personalised pattern analysis, research pain science topics, and receive evidence-based suggestions tailored to your health journey. It is available 24/7 and gets smarter the more you use it." } },
            { "@type": "Question", "name": "What makes Recalibrate different from other health apps?", "acceptedAnswer": { "@type": "Answer", "text": "Recalibrate is not just another tracker. It is an entire health ecosystem, the first of its kind, that replaces what would normally take five or more separate apps. You get advanced tracking across 18+ health variables with custom metrics like Stability Score, a Neuroscience Academy with 200+ lessons, therapeutic tools including CBT, mindfulness and guided exercises, AI-powered insights and data chat, journaling, goal tracking, medication management, and a connected Care Team portal where clinicians, psychologists, pain specialists and carers can access real-time patient data, maximising every appointment." } },
            { "@type": "Question", "name": "Is Recalibrate free?", "acceptedAnswer": { "@type": "Answer", "text": "There is a free tier - you get the Initial and Level 2 AI assessment, 10 tracking entries a month, Daily Habits + Streak, the Academy Course 1 intro and 5 Recalibrate AI credits a month. Unlimited tracking, the Stability Dashboard, monthly Level 2 reassessments (PDFs included) and the 136-lesson Academy foundations unlock at Recalibrate Go ($12.99/mo or $99.99/yr). Recalibrate Pro ($34.99/mo or $299.99/yr) adds the Academy Pro Courses (200+ lessons), Therapeutic Tools, AI Journal, medications and reminders, Brain Games, predictive analytics and a 2 clinician + 2 carer Care Team. New users can claim 3 months of Pro for $34.99 (pay 1, get 3) via the Welcome Offer." } },
            { "@type": "Question", "name": "Who is Recalibrate for?", "acceptedAnswer": { "@type": "Answer", "text": "Recalibrate is built for three audiences. Patients living with chronic pain, fibromyalgia, long COVID, ME-CFS, ADHD, central sensitisation and other long-term health conditions. Clinicians including physiotherapists, psychologists, pain specialists and allied health professionals who want real-time patient data and analytics. And carers and family members who want to support their loved ones with shared health dashboards." } },
            { "@type": "Question", "name": "Is my health data private on Recalibrate?", "acceptedAnswer": { "@type": "Answer", "text": "100% private. Your data is encrypted, never sold and never shared. You own your data completely. We follow strict industry-standard security practices and will never monetise your health information." } }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "How to Manage Chronic Pain with Recalibrate",
          "description": "The 8-step Recalibrate ecosystem from onboarding to connected care for chronic pain management.",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "AI Assessments", "text": "Complete initial and in-depth health profile assessments to establish your baseline." },
            { "@type": "HowToStep", "position": 2, "name": "Daily Check-Ins", "text": "Log daily symptoms and wellbeing with quick check-in tools." },
            { "@type": "HowToStep", "position": 3, "name": "Track Your Health", "text": "Monitor 18+ variables across physical, mental and lifestyle dimensions." },
            { "@type": "HowToStep", "position": 4, "name": "Medication Tracking", "text": "Track medications, dosages and set smart reminders." },
            { "@type": "HowToStep", "position": 5, "name": "Neuroscience Academy", "text": "Access 200+ lessons on health and pain neuroscience." },
            { "@type": "HowToStep", "position": 6, "name": "Therapeutic Tools", "text": "Use journals, exercises and guided therapeutic activities." },
            { "@type": "HowToStep", "position": 7, "name": "AI Insights", "text": "Receive personalised patterns and smart suggestions from Recalibrate AI." },
            { "@type": "HowToStep", "position": 8, "name": "Connect Care Team", "text": "Share reports and data with your clinicians and family carers." }
          ],
          "tool": [{ "@type": "HowToTool", "name": "Recalibrate App", "url": "https://recalibratepain.app" }]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Recalibrate Inc.",
          "alternateName": "Recalibrate",
          "url": "https://www.recalibratepain.com",
          "logo": { "@type": "ImageObject", "url": "https://www.recalibratepain.com/favicon-512.png", "width": 512, "height": 512 },
          "description": "Building the world's most comprehensive allied health platform for chronic pain, chronic illness and rehabilitation.",
          "email": "info@recalibratepain.com",
          "foundingDate": "2024",
          "knowsAbout": ["chronic pain management", "fibromyalgia", "long COVID", "ME-CFS", "ADHD", "central sensitisation", "pain neuroscience", "health tracking", "allied health", "patient care technology"],
          "sameAs": ["https://www.instagram.com/recalibrateapp/", "https://www.linkedin.com/company/recalibrate-app/", "https://x.com/RecalibrateApp", "https://za.pinterest.com/Recalibratepain/"],
          "contactPoint": { "@type": "ContactPoint", "contactType": "customer support", "email": "info@recalibratepain.com", "availableLanguage": "English" }
        })}</script>
      </Helmet>
      <Toaster position="top-center" toastOptions={{ style: { borderRadius: '12px', background: '#1f2937', color: '#fff' } }} />
      <Navbar />

      {/* Hero Section */}
      <section id="main-content" className="relative min-h-[82vh] sm:min-h-[88vh] flex flex-col justify-center px-4 sm:px-6 overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
        {/* Layered gradient background */}
        <div ref={heroBgRef} className="absolute inset-0 overflow-hidden pointer-events-none will-change-transform">
          {/* Neural photographic base layer */}
          <picture>
            <source srcSet="/hero-neural-bg.webp" type="image/webp" />
            <img
              src="/hero-neural-bg.jpg"
              alt=""
              aria-hidden="true"
              className="hero-neural-base absolute inset-0 w-full h-full object-cover"
              style={{ opacity: 0.5, mixBlendMode: 'screen' }}
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/40 to-slate-950/80"></div>
          {/* Animated gradient mesh */}
          <div className="hero-mesh-gradient absolute inset-0" style={{ opacity: 0.6 }}></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-[#6716be]/20 via-transparent to-[#4a90e2]/10"></div>
          {/* Animated orbs with brand purple */}
          <div className="hero-orb hero-orb-1"></div>
          <div className="hero-orb hero-orb-2"></div>
          <div className="hero-orb hero-orb-3"></div>
          <div className="hero-orb hero-orb-4"></div>
          {/* Grid overlay */}
          <div className="hero-grid-overlay"></div>
          {/* Floating R letters */}
          <div className="hero-r hero-r-2" style={{ color: 'rgba(167, 139, 250, 0.12)' }}>R</div>
          <div className="hero-r hero-r-3" style={{ color: 'rgba(167, 139, 250, 0.1)' }}>R</div>
          <div className="hero-r hero-r-6" style={{ color: 'rgba(167, 139, 250, 0.08)' }}>R</div>
          {/* Radial glow behind text */}
          <div ref={heroGlowRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#6716be]/15 rounded-full blur-[120px] will-change-transform"></div>
        </div>

        <div ref={heroContentRef} className="max-w-7xl mx-auto relative z-10 pt-24 sm:pt-20 pb-20 sm:pb-16 will-change-transform">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center max-w-6xl mx-auto">

            {/* LEFT - copy */}
            <div className="text-center lg:text-left">
              {/* Brand wordmark - now the hero anchor */}
              <div className="hero-stagger-2 mb-5 sm:mb-6 flex items-center justify-center lg:justify-start gap-3">
                <span className="hero-brand-font relative inline-block font-black leading-none select-none whitespace-nowrap" style={{ fontSize: 'clamp(2.9rem, 14.5vw, 7rem)', letterSpacing: '-0.035em' }}>
                  <span className="brand-flow relative bg-gradient-to-r from-[#4d8df7] via-[#7c6cf7] to-[#9b44f0] bg-clip-text text-transparent" style={{ WebkitTextFillColor: 'transparent' }}>Recalibrate</span>
                  <span aria-hidden="true" style={{ position: 'absolute', top: '-0.04em', left: 'calc(100% + 0.04em)', fontSize: '0.38em', color: '#34d399', fontWeight: 900, textShadow: '0 0 12px rgba(52,211,153,0.6)' }}>+</span>
                </span>
              </div>

              <h1 className="hero-stagger-3 text-[1.6rem] sm:text-3xl lg:text-[2.45rem] font-bold text-white leading-[1.18] mb-3 sm:mb-4 max-w-xl mx-auto lg:mx-0" data-testid="hero-title">
                Your health deserves <span className="block brand-flow bg-gradient-to-r from-[#4d8df7] via-[#7c6cf7] to-[#9b44f0] bg-clip-text text-transparent">a clearer picture.</span>
              </h1>
              <p data-testid="hero-value-proposition" className="text-sm sm:text-base text-purple-100/90 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-5">Health tracking, neuroscience education and practical tools in one app, built to help you understand your patterns.</p>

              {/* Ecosystem preview chips - tells a cold visitor what is further down the page */}
              <div className="hero-stagger-3b mt-5 sm:mt-6 mb-6 sm:mb-7 hidden md:flex flex-wrap justify-center lg:justify-start gap-1.5 sm:gap-2" data-testid="hero-ecosystem-chips">
                {[
                  { icon: BarChart3, label: 'Smart Tracking' },
                  { icon: BookOpen, label: '200+ Lessons' },
                  { icon: Heart, label: 'Therapeutic Tools' },
                  { icon: Zap, label: 'Recalibrate AI' },
                  { icon: Stethoscope, label: 'Care Team' },
                  { icon: Users, label: 'Community' },
                  { icon: FileText, label: 'Free Starter Kit', href: '#starter-kit' },
                  { icon: Globe, label: 'Health Portals' },
                ].map(({ icon: Icon, label, href }) => {
                  const Tag = href ? 'a' : 'span';
                  return (
                  <Tag
                    key={label}
                    href={href}
                    data-testid={href ? 'hero-pill-starter-kit' : undefined}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 text-[11px] sm:text-xs font-semibold text-purple-50/85 backdrop-blur-sm transition-colors hover:border-white/30 hover:bg-white/[0.12]"
                  >
                    <Icon className="w-3.5 h-3.5 text-[#a78bfa]" />
                    {label}
                  </Tag>
                  );
                })}
              </div>

              <div className="hero-stagger-5 flex flex-col items-center lg:items-start gap-3.5 w-full">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  <a
                    href={isIos ? APP_STORE_URL : isAndroid ? storeUrl('hero') : appUrl('/', 'hero-start-free', 'home')}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-start-free-btn"
                    className="cta-shimmer cta-pulse-glow group relative bg-gradient-to-r from-blue-500 to-purple-600 text-white px-9 sm:px-10 py-4 rounded-2xl font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-purple-600/30 transition-all flex items-center justify-center gap-2 hover:-translate-y-1"
                  >
                    <span>{isAndroid || isIos ? 'Get the app free' : 'Get started for free'}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <Link
                    to="/features"
                    data-testid="hero-explore-btn"
                    className="group hidden sm:flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/[0.06] px-7 py-4 font-semibold text-white/90 backdrop-blur-sm transition-colors hover:border-white/40 hover:bg-white/[0.12] hover:text-white"
                  >
                    <span>Explore the app</span>
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>

                {/* App store badges. Phone visitors keep a browser option since
                    their primary CTA goes to their store listing. */}
                <div className="mt-1">
                  <StoreBadges align="lg-left" web={isAndroid || isIos} testidPrefix="hero-store" />
                </div>
              </div>
            </div>

            {/* RIGHT - animated "recalibrate" signal display */}
            <div className="hero-stagger-4 relative mt-2 lg:mt-0 hidden lg:block" data-testid="hero-signal-display">
              <div className="relative rounded-[1.75rem] border border-white/10 bg-white/[0.04] backdrop-blur-md p-5 sm:p-7 overflow-hidden shadow-2xl shadow-purple-900/40">
                <span aria-hidden="true" className="pointer-events-none select-none absolute -right-8 -top-12 font-black text-white/[0.045]" style={{ fontSize: '15rem', lineHeight: 1 }}>R</span>

                <div className="relative flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold tracking-[0.16em] uppercase text-rose-300/80">
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse"></span> Dysregulated
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-bold tracking-[0.16em] uppercase text-sky-300/90">
                    Recalibrated <span className="w-1.5 h-1.5 rounded-full bg-sky-400"></span>
                  </span>
                </div>

                <svg viewBox="0 0 600 240" className="relative w-full h-auto" role="img" aria-label="A stressed signal that smooths into a calm wave">
                  <defs>
                    <linearGradient id="sigGrad" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#f43f5e" />
                      <stop offset="40%" stopColor="#a855f7" />
                      <stop offset="100%" stopColor="#4d8df7" />
                    </linearGradient>
                    <filter id="sigGlow" x="-20%" y="-60%" width="140%" height="220%">
                      <feGaussianBlur stdDeviation="4" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>

                  <line x1="0" y1="120" x2="600" y2="120" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />

                  <path
                    id="sigPath"
                    className="signal-path"
                    fill="none"
                    stroke="url(#sigGrad)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter="url(#sigGlow)"
                    d="M0,120 L54,120 L74,52 L94,196 L114,64 L134,170 L154,120 C214,120 244,52 322,98 C394,140 432,78 512,110 C554,124 578,118 600,118"
                  />

                  <circle className="signal-node" cx="154" cy="120" r="4.5" fill="#f43f5e" style={{ animationDelay: '1.4s' }} />
                  <circle className="signal-node" cx="322" cy="98" r="5" fill="#a855f7" style={{ animationDelay: '0s' }} />
                  <circle className="signal-node" cx="512" cy="110" r="5" fill="#4d8df7" style={{ animationDelay: '0.8s' }} />

                  <circle r="6" fill="#ffffff" filter="url(#sigGlow)">
                    <animateMotion dur="4s" repeatCount="indefinite" calcMode="linear">
                      <mpath href="#sigPath" />
                    </animateMotion>
                  </circle>
                </svg>

                <div className="relative mt-5 hidden sm:grid grid-cols-2 gap-3 sm:gap-4">
                  {[['300+', 'Health Tools & Lessons'], ['7 Days', 'Free on iPhone & Android']].map(([n, l], i) => (
                    <div
                      key={i}
                      className="group relative overflow-hidden rounded-2xl border border-white/20 bg-gradient-to-br from-white/[0.12] to-white/[0.04] px-3 py-4 text-center shadow-lg shadow-purple-900/30 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-white/30 hover:shadow-xl hover:shadow-purple-600/30"
                      data-testid={`hero-stat-${i}`}
                    >
                      <div className="pointer-events-none absolute -top-8 -right-8 h-20 w-20 rounded-full bg-gradient-to-br from-[#4d8df7]/40 to-[#9b44f0]/40 blur-2xl"></div>
                      <div className="relative text-2xl sm:text-3xl font-black bg-gradient-to-r from-[#5b9bff] to-[#b15cff] bg-clip-text text-transparent drop-shadow-[0_2px_10px_rgba(155,68,240,0.35)]">{n}</div>
                      <div className="relative mt-1 text-[11px] sm:text-xs text-purple-100/80 font-semibold leading-tight tracking-wide">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>

      </section>

      <section
        id="ecosystem-tour"
        data-animate
        data-testid="ecosystem-tour-section"
        className="relative z-[5] mt-2 sm:-mt-14 rounded-t-[2rem] sm:rounded-t-[3rem] overflow-hidden bg-gradient-to-b from-purple-50 via-white to-indigo-50/60 py-8 sm:py-24 px-4 sm:px-6"
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="hub-breathe-1 absolute top-10 -left-16 w-72 h-72 bg-purple-300/25 rounded-full blur-3xl"></div>
          <div className="hub-breathe-3 absolute bottom-0 -right-16 w-80 h-80 bg-indigo-300/25 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-5xl mx-auto">
          <div className="text-center mb-5 sm:mb-14 max-w-2xl mx-auto" data-tour-head style={{ willChange: 'transform, opacity' }}>
            <span className="inline-block bg-gradient-to-r from-purple-500/10 to-violet-500/10 text-purple-700 text-sm font-semibold px-4 py-2 rounded-full mb-4 border border-purple-200">One app. Everything connected.</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3" data-testid="ecosystem-tour-title">
              Your whole ecosystem, <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">in one app</span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Over 300 tools across Neuroscience, Psychology, Systems Biology and Pain Science. Here is what lives inside Recalibrate.
            </p>
          </div>

          {/* Tablet + Desktop: phone centered, labels float around it */}
          <div className="relative hidden sm:block max-w-5xl mx-auto" style={{ minHeight: 470 }}>
            <div className="relative mx-auto w-[200px] h-[420px] lg:w-[230px] lg:h-[470px] z-10" style={{ perspective: '1200px' }}>
              <div className="w-full h-full" data-phone-spin style={{ willChange: 'transform, opacity', transformStyle: 'preserve-3d' }}>
                <div className="phone-breathe relative w-full h-full bg-gray-900 rounded-[2.8rem] lg:rounded-[3rem] p-2.5 shadow-2xl shadow-purple-500/25">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 lg:w-24 h-5 lg:h-6 bg-gray-900 rounded-b-2xl z-10"></div>
                  <div className="w-full h-full bg-white rounded-[2.3rem] overflow-hidden">
                    <img src="/app-screenshot-optimized.jpg" alt="Recalibrate App" className="w-full h-full object-cover object-top" width="560" height="1102" loading="eager" fetchpriority="high" decoding="async" />
                  </div>
                </div>
              </div>
            </div>

            {[
              { side: 'right', pct: '78%', top: '4%', accent: '#3b82f6', icon: '🛠️', title: '100+ Health Tools', sub: 'Track & manage health', anim: 'label-drift-1' },
              { side: 'right', pct: '74%', top: '28%', accent: '#10b981', icon: '🎓', title: 'Academy', sub: '200+ Lessons', anim: 'label-drift-2' },
              { side: 'right', pct: '70%', top: '52%', accent: '#ef4444', icon: '🧠', title: 'Therapeutic Tools', sub: 'AI Journal, CBT & Exercises', anim: 'label-drift-3' },
              { side: 'right', pct: '66%', top: '74%', accent: '#8b5cf6', icon: '⚡', title: 'Recalibrate AI', sub: 'Chat with your data', anim: 'label-drift-4' },
              { side: 'left', pct: '78%', top: '4%', accent: '#06b6d4', icon: '🩺', title: 'Clinician Dashboard', sub: 'Multi-patient dashboard & live data', anim: 'label-drift-5' },
              { side: 'left', pct: '74%', top: '28%', accent: '#d946ef', icon: '👨‍👩‍👧', title: 'Carer Dashboard', sub: 'Family support portal', anim: 'label-drift-6' },
              { side: 'left', pct: '70%', top: '52%', accent: '#0ea5e9', icon: '💬', title: 'Messenger', sub: 'Real-time health chat', anim: 'label-drift-7' },
              { side: 'left', pct: '66%', top: '74%', accent: '#6366f1', icon: '🌐', title: 'Health Portals', sub: 'Condition intelligence', anim: 'label-drift-8' },
            ].map((l, i) => (
              <div
                key={i}
                data-label-fly
                data-side={l.side}
                data-idx={i}
                className="absolute z-20"
                style={{ top: l.top, [l.side]: l.pct, width: 220, willChange: 'transform, opacity' }}
              >
                <div className={l.anim}>
                  <div className="relative rounded-2xl overflow-hidden bg-white" style={{ border: `3.5px solid ${l.accent}`, boxShadow: `0 8px 26px ${l.accent}33, 0 2px 6px rgba(0,0,0,0.06)`, height: 56 }}>
                    <div className="relative p-2.5 lg:p-3 flex items-center gap-2.5 h-full">
                      <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: l.accent, boxShadow: `0 3px 10px ${l.accent}50` }}>
                        <span className="text-base lg:text-lg">{l.icon}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-black text-xs lg:text-[13px] leading-tight truncate" style={{ color: l.accent }}>{l.title}</p>
                        <p className="text-[10px] lg:text-[11px] leading-tight mt-0.5 font-semibold text-gray-500 truncate">{l.sub}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: same composition as desktop - phone spins + fades in with
              tool labels flying in alongside it. Normal flow (no pinning). */}
          <div className="sm:hidden flex items-stretch justify-center gap-1.5 px-0.5 py-2">
            <div className="flex flex-col justify-center flex-1 min-w-0 gap-2.5">
              {[
                { icon: '🛠️', label: '100+ Tools', sub: 'Track & manage', accent: '#3b82f6' },
                { icon: '🎓', label: 'Academy', sub: '200+ Lessons', accent: '#10b981' },
                { icon: '🧠', label: 'Therapeutic', sub: 'Journal & CBT', accent: '#ef4444' },
                { icon: '🩺', label: 'Clinician', sub: 'Multi-patient', accent: '#06b6d4' },
              ].map((item, i) => (
                <div key={i} data-label-fly data-side="left" data-idx={i} className="rounded-xl bg-white flex items-center gap-1.5 p-1.5 relative" style={{ border: `2.5px solid ${item.accent}`, boxShadow: `0 6px 16px ${item.accent}33`, willChange: 'transform, opacity' }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: item.accent }}>
                    <span className="text-sm">{item.icon}</span>
                  </div>
                  <div className="min-w-0 flex-1"><p className="text-[9px] font-black leading-tight" style={{ color: item.accent }}>{item.label}</p><p className="text-[8px] font-semibold text-gray-500 leading-snug">{item.sub}</p></div>
                </div>
              ))}
            </div>

            <div className="flex-shrink-0 self-center" style={{ perspective: '900px' }}>
              <div className="w-[120px] h-[258px]" data-phone-spin style={{ willChange: 'transform, opacity', transformStyle: 'preserve-3d' }}>
                <div className="phone-breathe relative w-full h-full bg-gray-900 rounded-[1.8rem] p-1.5 shadow-2xl shadow-purple-500/25 z-10">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-3.5 bg-gray-900 rounded-b-xl z-10"></div>
                  <div className="w-full h-full bg-white rounded-[1.5rem] overflow-hidden">
                    <img src="/app-screenshot-optimized.jpg" alt="Recalibrate App" className="w-full h-full object-cover object-top" width="296" height="610" loading="eager" fetchpriority="high" decoding="async" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center flex-1 min-w-0 gap-2.5">
              {[
                { icon: '⚡', label: 'Recalibrate AI', sub: 'Chat your data', accent: '#8b5cf6' },
                { icon: '💬', label: 'Messenger', sub: 'Real-time chat', accent: '#0ea5e9' },
                { icon: '👨‍👩‍👧', label: 'Carer', sub: 'Family portal', accent: '#d946ef' },
                { icon: '🌐', label: 'Portals', sub: 'Condition intel', accent: '#6366f1' },
              ].map((item, i) => (
                <div key={i} data-label-fly data-side="right" data-idx={i} className="rounded-xl bg-white flex items-center gap-1.5 p-1.5 relative" style={{ border: `2.5px solid ${item.accent}`, boxShadow: `0 6px 16px ${item.accent}33`, willChange: 'transform, opacity' }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: item.accent }}>
                    <span className="text-sm">{item.icon}</span>
                  </div>
                  <div className="min-w-0 flex-1"><p className="text-[9px] font-black leading-tight" style={{ color: item.accent }}>{item.label}</p><p className="text-[8px] font-semibold text-gray-500 leading-snug">{item.sub}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 sm:mt-14 flex justify-center" data-tour-cta style={{ willChange: 'transform, opacity' }}>
            <a
              href={appUrl('/', 'ecosystem-tour-open-app', 'home')}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ecosystem-tour-cta"
              className="cta-shimmer group inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-sm sm:text-base hover:shadow-2xl hover:shadow-purple-600/30 hover:-translate-y-1 transition-all"
            >
              <span>Explore the full ecosystem</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>


      {/* Thin moving ticker - bridges the light ecosystem section into the dark one below */}
      <div className="hero-ticker hero-ticker--bridge relative overflow-hidden" data-testid="hero-ticker">
        <div className="hero-ticker-track flex w-max items-center py-2.5">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 items-center" aria-hidden={copy === 1}>
              {['Nervous system regulation', 'Health & neuroscience', 'Chronic pain & chronic illness', 'Everyday wellbeing', 'Tracking that makes sense', 'Free to start'].map((label) => (
                <span key={label} className="flex items-center whitespace-nowrap text-[9.5px] sm:text-[11px] font-bold uppercase tracking-[0.24em] text-purple-800/70">
                  {label}
                  <span className="mx-5 sm:mx-8 text-purple-500/50" aria-hidden="true">&middot;</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <section className="py-12 sm:py-16 bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 overflow-hidden relative" data-testid="evidence-based-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" data-testid="evidence-based-title">
              Built on <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Real Science</span>, Not Wellness Trends
            </h2>
            <p className="text-purple-200/80 text-sm sm:text-base">Every tracker, lesson and tool is grounded in pain neuroscience, physiotherapy, psychology and sleep research.</p>
          </div>
        </div>

        <div className="relative z-10">
          <AppScreenshots placement="home" dark />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div
            id="evidence-grid"
            data-animate
            data-testid="evidence-grid"
            className={`stagger-reveal grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 ${visibleSections['evidence-grid'] ? 'is-visible' : ''}`}
          >
            {[
              { icon: '🧠', label: 'Pain Neuroscience', color: 'from-blue-500 to-indigo-500' },
              { icon: '💭', label: 'CBT Therapy', color: 'from-purple-500 to-violet-500' },
              { icon: '🧘', label: 'Mindfulness', color: 'from-emerald-500 to-teal-500' },
              { icon: '🏃', label: 'Movement Therapy', color: 'from-orange-500 to-amber-500' },
              { icon: '📈', label: 'Graded Exposure', color: 'from-pink-500 to-rose-500' },
              { icon: '😴', label: 'Sleep Science', color: 'from-indigo-500 to-blue-500' },
            ].map((item, i) => (
              <div key={i} className="stagger-item bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 text-center hover:bg-white/15 transition-all hover:-translate-y-1">
                <div className={`w-12 h-12 mx-auto mb-2 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-xl shadow-lg`}>{item.icon}</div>
                <p className="text-xs sm:text-sm font-semibold text-white">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10" data-testid="evidence-tags">
            {['Acceptance & Commitment', 'Pacing Strategies', 'Nutrition Science', 'Stress Management', 'Biofeedback', 'Goal Setting', 'Self-Compassion', 'Activity Tracking', 'Therapeutic Alliance', 'Neuroplasticity', 'Systems Biology', 'Precision Medicine'].map((item, i) => (
              <span key={i} className="bg-white/10 backdrop-blur text-purple-200 border border-purple-500/30 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium hover:bg-white/20 hover:border-purple-400/50 hover:text-white transition-all cursor-default">{item}</span>
            ))}
          </div>

          {/* Get Started CTA */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href={appUrl('/', 'evidence-open-app', 'home')}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="how-it-works-get-started-btn"
              className="cta-shimmer group relative bg-gradient-to-r from-blue-500 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-purple-600/30 transition-all flex items-center justify-center gap-2 hover:-translate-y-1"
            >
              <span>Get started for free</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/features"
              data-testid="evidence-see-features-btn"
              className="group relative bg-white/10 border border-white/15 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-white/15 transition-all flex items-center justify-center gap-2 hover:-translate-y-1 backdrop-blur-sm"
            >
              <span>See how it works</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>


      <StarterKitSignup />

      <PassionShorts visible={visibleSections['passion-section']} />

      <HomeShowcase />

      {/* Final CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 rounded-3xl p-8 sm:p-12 text-center overflow-hidden">
            <div className="absolute top-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-1.5 mb-6" data-testid="final-cta-trial-pill">
                <Sparkles className="w-4 h-4 text-slate-900" />
                <span className="text-slate-900 font-extrabold text-xs sm:text-sm uppercase tracking-[0.12em]">7 days free &middot; $0 today</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Try all of Recalibrate free for 7 days</h2>
              <p className="text-lg text-white/80 mb-6 max-w-xl mx-auto">Now on iPhone and Android. Track, learn and manage your health in one place, with everything unlocked for your first week.</p>
              <ul className="inline-flex flex-col items-start gap-1.5 text-left text-sm sm:text-base text-white/90 mb-8" data-testid="final-cta-trial-points">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />Pay nothing for 7 days</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />Cancel anytime in two taps</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />Keep your data, even if you cancel</li>
              </ul>
              <div data-testid="final-cta-store-badges">
                <StoreBadges align="center" label={false} testidPrefix="final-cta-store" />
              </div>
              <p className="mt-6 text-xs text-white/50 max-w-lg mx-auto" data-testid="final-cta-trial-terms">Free for 7 days, then Go $12.99/month or Pro $34.99/month (US pricing, your store shows your local price) unless you cancel before the trial ends. New subscribers only.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 px-4 sm:px-6 scroll-mt-20 bg-gradient-to-b from-white via-purple-50/20 to-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Common questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-purple-100 rounded-2xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-50/50 transition-colors" data-testid={`faq-item-${i}`}>
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
                {openFaq === i && <div className="px-6 pb-4 text-gray-600">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default App;
